import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowUpRight, Bookmark, Check, GripVertical, Heart, Image as ImageIcon, Layers,
  MessageCircle, MousePointer2, MoveRight, PenTool, Pipette, Play, Send, ShoppingBag,
  Square, Star, Type, Youtube,
} from "lucide-react";
import { SKILL_PACKS } from "../data/content";
import { TopoLines, Reveal, SectionHeader } from "./ui-bits";

const CODES = { design: "DS", web: "WB", growth: "GR", content: "CT" };

const ALL = SKILL_PACKS.flatMap((p) =>
  p.skills.map((s, i) => ({
    s,
    pack: p.id,
    packName: p.name,
    code: `${CODES[p.id]}-${String(i + 1).padStart(2, "0")}`,
  }))
);

/* ── 15 live artifacts — one per tool, each drawn in code, no images ── */

function AdobeCanvas() {
  return (
    <ArtShell label="CANVAS — ADOBE SUITE & CANVA" meta="EDIT · 100%">
      <div className="flex h-full gap-2 p-2.5">
        {/* tool rail */}
        <div className="flex w-8 shrink-0 flex-col items-center gap-1 rounded-md border border-cream/10 bg-night/70 py-2">
          {[MousePointer2, Type, Square, PenTool, Pipette].map((Icon, i) => (
            <motion.span
              key={i} initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.1 + i * 0.06 }}
              className={`flex h-6 w-6 items-center justify-center rounded-[4px] ${i === 0 ? "bg-ember text-night shadow-[0_0_12px_rgba(232,90,36,0.5)]" : "text-cream/50"}`}
            >
              <Icon className="h-3 w-3" />
            </motion.span>
          ))}
        </div>
        {/* canvas — mini poster under a live selection */}
        <div className="relative min-w-0 flex-1 overflow-hidden rounded-md border border-cream/15 bg-night/60">
          <motion.div
            initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.25, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-[14%] flex flex-col justify-center gap-1.5 overflow-hidden rounded-[3px] bg-gradient-to-br from-ember/85 via-ember/55 to-[#7a2a0c] p-2.5"
          >
            <TopoLines className="pointer-events-none absolute inset-0 h-full w-full opacity-20" />
            <span className="relative h-1.5 w-3/5 rounded-full bg-night/85" />
            <span className="relative h-1.5 w-2/5 rounded-full bg-night/60" />
            <span className="relative mt-1 h-1 w-4/5 rounded-full bg-cream/70" />
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.75 }} className="absolute inset-[14%] border border-dashed border-cream/80">
            {["-left-[3px] -top-[3px]", "-right-[3px] -top-[3px]", "-left-[3px] -bottom-[3px]", "-right-[3px] -bottom-[3px]"].map((p) => (
              <span key={p} className={`absolute ${p} h-1.5 w-1.5 rounded-[1px] border border-cream bg-night`} />
            ))}
            <span className="absolute -top-4 left-0 bg-cream px-1 font-mono text-[6px] font-bold tracking-[0.15em] text-night">POSTER · 1080 × 1350</span>
          </motion.div>
        </div>
        {/* layers panel */}
        <div className="hidden w-16 shrink-0 flex-col gap-1 rounded-md border border-cream/10 bg-night/70 p-1.5 sm:flex">
          <span className="mb-0.5 flex items-center gap-1 font-mono text-[6px] tracking-[0.2em] text-cream/40"><Layers className="h-2.5 w-2.5" /> LAYERS</span>
          {["POSTER BG", "HEADLINE", "TOPO", "GRAIN"].map((l, i) => (
            <motion.span key={l} initial={{ opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 + i * 0.08 }} className={`flex items-center gap-1 rounded-[3px] px-1 py-[3px] font-mono text-[6px] tracking-[0.12em] ${i === 0 ? "bg-ember/20 text-ember" : "text-cream/45"}`}>
              <span className={`h-1.5 w-1.5 rounded-[1px] ${i === 0 ? "bg-ember" : "bg-cream/30"}`} />{l}
            </motion.span>
          ))}
        </div>
      </div>
    </ArtShell>
  );
}

function Wireframe() {
  return (
    <ArtShell label="BROWSER WIREFRAME — WEB DESIGN" meta="RESPONSIVE">
      <div className="flex h-full flex-col overflow-hidden rounded-md border border-cream/15 bg-ink">
        <div className="flex shrink-0 items-center gap-1.5 border-b border-cream/10 bg-night/60 px-3 py-1.5">
          <span className="h-2 w-2 rounded-full bg-ember" />
          <span className="h-2 w-2 rounded-full bg-cream/30" />
          <span className="h-2 w-2 rounded-full bg-cream/30" />
          <span className="ml-2 flex-1 rounded-full bg-cream/10 px-2.5 py-0.5 font-mono text-[8px] tracking-[0.2em] text-cream/50">AMITRAWAT.DESIGN</span>
        </div>
        <div className="min-h-0 flex-1 p-3">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-ember" />
            <span className="ml-auto h-1.5 w-8 rounded-full bg-cream/25" />
            <span className="h-1.5 w-8 rounded-full bg-cream/25" />
            <span className="h-4 w-12 rounded-sm bg-ember" />
          </div>
          <div className="mt-3 flex gap-3">
            <div className="flex min-w-0 flex-1 flex-col justify-center gap-1.5">
              <motion.span initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.15, duration: 0.5 }} className="h-2.5 w-full origin-left rounded-sm bg-cream/80" />
              <motion.span initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.25, duration: 0.5 }} className="h-2.5 w-3/5 origin-left rounded-sm bg-ember" />
              <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="h-1 w-4/5 rounded-full bg-cream/25" />
              <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="relative mt-1 flex h-5 w-20 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-ember font-mono text-[7px] font-bold tracking-[0.2em] text-night">
                START HERE
                <Sheen />
              </motion.span>
            </div>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="relative h-20 w-[38%] shrink-0 overflow-hidden rounded-md border border-cream/15 bg-gradient-to-br from-ember/30 to-transparent md:h-24">
              <TopoLines className="absolute inset-0 h-full w-full opacity-25" />
              <span className="absolute right-2 top-2 h-3 w-3 rounded-full bg-ember shadow-[0_0_10px_rgba(232,90,36,0.7)]" />
            </motion.div>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {[0, 1, 2].map((i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 + i * 0.1 }} className="rounded-md border border-cream/12 bg-night/50 p-1.5">
                <span className={`mb-1 block h-1 w-3/4 rounded-full ${i === 1 ? "bg-ember/80" : "bg-cream/40"}`} />
                <span className="block h-1 w-1/2 rounded-full bg-cream/20" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </ArtShell>
  );
}

function UIKit() {
  return (
    <ArtShell label="UI KIT — COMPONENTS & STATES" meta="SPEC 02">
      <div className="flex h-full flex-col justify-center gap-3.5 rounded-md border border-cream/10 bg-night/60 p-4">
        <div className="flex items-center gap-2.5">
          <motion.span initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="relative overflow-hidden bg-ember px-3.5 py-1.5 font-mono text-[8px] font-bold tracking-[0.2em] text-night">
            PRIMARY
            <Sheen />
          </motion.span>
          <motion.span initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }} className="border border-cream/30 px-3.5 py-1.5 font-mono text-[8px] tracking-[0.2em] text-cream/80">OUTLINE</motion.span>
          <span className="ml-auto hidden font-mono text-[7px] tracking-[0.15em] text-cream/35 sm:block">CTA · HOVER</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="relative h-5 w-9 rounded-full bg-ember shadow-[0_0_10px_rgba(232,90,36,0.45)]">
            <motion.span initial={{ x: 0 }} animate={{ x: 16 }} transition={{ delay: 0.6, type: "spring", stiffness: 300 }} className="absolute top-0.5 h-4 w-4 rounded-full bg-night" />
          </span>
          <span className="font-mono text-[7px] tracking-[0.15em] text-cream/40">ON</span>
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }} className="flex h-5 w-5 items-center justify-center border border-ember bg-ember/20 text-ember">
            <Check className="h-3.5 w-3.5" />
          </motion.span>
          <div className="relative flex-1">
            <span className="absolute -top-2.5 left-[62%] -translate-x-1/2 font-mono text-[6px] font-bold tracking-[0.1em] text-cream/60">62%</span>
            <div className="h-1 rounded-full bg-cream/15" />
            <motion.div initial={{ width: 0 }} animate={{ width: "62%" }} transition={{ delay: 0.55, duration: 0.6 }} className="absolute top-0 h-1 rounded-full bg-ember" />
            <motion.span initial={{ left: 0 }} animate={{ left: "62%" }} transition={{ delay: 0.55, duration: 0.6 }} className="absolute -top-1 h-3 w-3 -translate-x-1/2 rounded-full bg-cream shadow-[0_0_8px_rgba(245,240,225,0.5)]" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="flex min-w-0 flex-1 items-center border border-ember/60 bg-ink/60 px-3 py-1.5 shadow-[inset_0_0_0_1px_rgba(232,90,36,0.15)]">
            <span className="font-mono text-[8px] tracking-[0.15em] text-cream/40">TYPE HERE</span>
            <span className="ml-0.5 h-3 w-[2px] animate-pulse bg-ember" />
          </motion.div>
          <span className="flex shrink-0 gap-1">
            {["bg-ember", "bg-cream/70", "bg-[#D9A441]", "bg-night border border-cream/30"].map((c, i) => (
              <motion.span key={i} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.65 + i * 0.06 }} className={`h-3.5 w-3.5 rounded-full ${c}`} />
            ))}
          </span>
          <span className="shrink-0 border border-cream/25 px-1.5 py-0.5 font-display text-[9px] font-black text-cream/70">Aa</span>
        </div>
      </div>
    </ArtShell>
  );
}

function SocialPost() {
  return (
    <ArtShell label="POST MOCK — SOCIAL CREATIVE" meta="9:16 · FEED">
      <div className="flex h-full items-center justify-center">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="w-full max-w-[230px] overflow-hidden rounded-lg border border-cream/15 bg-ink shadow-[0_16px_32px_-18px_rgba(0,0,0,0.95)]">
          <div className="flex items-center gap-2 px-3 py-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-ember/80 font-mono text-[7px] font-black text-night">A</span>
            <span className="h-1.5 w-16 rounded-sm bg-cream/50" />
            <span className="ml-auto font-mono text-[7px] font-bold tracking-[0.15em] text-ember">FOLLOW</span>
          </div>
          <div className="relative h-24 overflow-hidden bg-gradient-to-br from-ember/55 via-ember/20 to-night md:h-28">
            <TopoLines className="absolute inset-0 h-full w-full opacity-25" />
            <motion.span animate={{ opacity: [0.75, 1, 0.75], scale: [1, 1.08, 1] }} transition={{ duration: 3, repeat: Infinity }} className="absolute right-3 top-3 h-6 w-6 rounded-full bg-ember shadow-[0_0_16px_rgba(232,90,36,0.8)]" />
            <span className="absolute bottom-2.5 left-3 h-1.5 w-1/3 rounded-sm bg-cream/85" />
          </div>
          <div className="flex items-center gap-3 px-3 py-2">
            <motion.span animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 1.6, repeat: Infinity, repeatDelay: 0.7 }} className="text-ember">
              <Heart className="h-4 w-4 fill-current" />
            </motion.span>
            <MessageCircle className="h-3.5 w-3.5 text-cream/60" />
            <Send className="h-3.5 w-3.5 text-cream/60" />
            <Bookmark className="ml-auto h-3.5 w-3.5 text-cream/60" />
          </div>
          <div className="px-3 pb-2.5">
            <span className="block h-1.5 w-24 rounded-sm bg-cream/40" />
          </div>
        </motion.div>
      </div>
    </ArtShell>
  );
}

function WordPressEditor() {
  return (
    <ArtShell label="PAGE EDITOR — WORDPRESS" meta="DRAFT · AUTOSAVED">
      <div className="flex h-full flex-col overflow-hidden rounded-md border border-cream/15 bg-ink">
        <div className="flex shrink-0 items-center gap-2 border-b border-cream/10 bg-night/60 px-3 py-1.5">
          <span className="flex h-5 w-5 items-center justify-center rounded-full border border-ember font-display text-[9px] font-black text-ember">W</span>
          <span className="h-1.5 w-24 rounded-sm bg-cream/25" />
          <span className="ml-auto relative overflow-hidden bg-ember px-2.5 py-0.5 font-mono text-[7px] font-bold tracking-[0.15em] text-night">
            PUBLISH
            <Sheen />
          </span>
        </div>
        <div className="min-h-0 flex-1 space-y-2 p-3.5">
          <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.15, duration: 0.45 }} className="h-3 w-1/2 origin-left rounded-sm bg-cream/70" />
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="space-y-1">
            <div className="h-1.5 w-full rounded-sm bg-cream/20" />
            <div className="h-1.5 w-5/6 rounded-sm bg-cream/20" />
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }} className="relative h-14 w-full overflow-hidden rounded-sm border border-cream/12 bg-gradient-to-br from-ember/30 to-cream/5">
            <TopoLines className="absolute inset-0 h-full w-full opacity-20" />
            <span className="absolute bottom-1.5 left-2 h-1 w-1/3 rounded-full bg-cream/70" />
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="flex items-center gap-2">
            <span className="flex h-4 w-4 items-center justify-center rounded-[3px] border border-dashed border-cream/30 font-mono text-[8px] leading-none text-cream/50">+</span>
            <span className="flex h-4 w-4 items-center justify-center rounded-[3px] border border-dashed border-cream/30 font-mono text-[8px] leading-none text-cream/50">+</span>
            <span className="ml-auto font-mono text-[6px] tracking-[0.2em] text-cream/35">SAVED 09:41</span>
          </motion.div>
        </div>
      </div>
    </ArtShell>
  );
}

function ShopifyStore() {
  return (
    <ArtShell label="STOREFRONT — SHOPIFY" meta="CONVERSION READY">
      <div className="flex h-full items-center justify-center">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="w-full max-w-[210px] overflow-hidden rounded-lg border border-cream/15 bg-ink shadow-[0_16px_32px_-18px_rgba(0,0,0,0.95)]">
          <div className="relative flex h-20 items-center justify-center overflow-hidden bg-gradient-to-br from-cream/10 to-ember/25">
            <TopoLines className="absolute inset-0 h-full w-full opacity-20" />
            <ShoppingBag className="h-7 w-7 text-cream/80" />
            <span className="absolute left-2 top-2 bg-ember px-1.5 py-0.5 font-mono text-[6px] font-bold tracking-[0.2em] text-night">NEW</span>
            <motion.span animate={{ scale: [1, 1.25, 1] }} transition={{ duration: 2, repeat: Infinity, repeatDelay: 0.5 }} className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-ember font-mono text-[8px] font-bold text-night shadow-[0_0_10px_rgba(232,90,36,0.6)]">2</motion.span>
          </div>
          <div className="space-y-1.5 p-3">
            <div className="flex items-center justify-between">
              <span className="h-2 w-3/5 rounded-sm bg-cream/60" />
              <span className="font-mono text-[9px] font-bold text-ember">$48</span>
            </div>
            <div className="flex items-center gap-1">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className={`h-2.5 w-2.5 ${i < 4 ? "fill-ember text-ember" : "text-cream/25"}`} />
              ))}
              <span className="ml-1 font-mono text-[6px] tracking-[0.15em] text-cream/40">128 REVIEWS</span>
            </div>
            <div className="relative flex h-6 items-center justify-center overflow-hidden bg-ember font-mono text-[8px] font-bold tracking-[0.2em] text-night">
              ADD TO CART
              <Sheen />
            </div>
          </div>
        </motion.div>
      </div>
    </ArtShell>
  );
}

function ElementorDrag() {
  return (
    <ArtShell label="WIDGET DROP — ELEMENTOR" meta="NO-CODE">
      <div className="relative h-full p-3">
        <div className="flex h-full gap-2.5">
          <div className="flex w-[30%] flex-col gap-1.5 rounded-md border border-cream/12 bg-night/70 p-2">
            <span className="font-mono text-[6px] tracking-[0.2em] text-cream/40">WIDGETS</span>
            {[Type, ImageIcon, Square].map((Icon, i) => (
              <span key={i} className="flex items-center gap-1.5 rounded-[3px] border border-cream/10 bg-ink/70 px-1.5 py-1">
                <Icon className="h-2.5 w-2.5 text-ember/80" />
                <span className="h-1 flex-1 rounded-full bg-cream/25" />
              </span>
            ))}
          </div>
          <div className="min-w-0 flex-1 rounded-md border border-cream/12 bg-ink/60 p-2">
            <div className="h-2 w-3/4 rounded-sm bg-cream/40" />
            <div className="mt-1.5 h-6 rounded-sm bg-cream/10" />
          </div>
        </div>
        <motion.div animate={{ opacity: [0.35, 1, 1, 0.35] }} transition={{ duration: 3, repeat: Infinity }} className="absolute right-6 top-[16%] h-[58%] w-[34%] rounded-md border-2 border-dashed border-ember/80 bg-ember/5 p-2">
          <span className="font-mono text-[6px] tracking-[0.2em] text-ember/90">DROP ZONE</span>
          <motion.span animate={{ opacity: [0, 0, 1, 0] }} transition={{ duration: 3, repeat: Infinity }} className="absolute bottom-2 left-2 flex items-center gap-1 font-mono text-[6px] font-bold tracking-[0.2em] text-ember">
            <Check className="h-2.5 w-2.5" /> PLACED
          </motion.span>
        </motion.div>
        <motion.div
          animate={{ x: [0, 100, 100, 0], y: [0, -20, -20, 0], opacity: [1, 1, 0.75, 1] }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          className="absolute left-6 top-1/2 flex -translate-y-1/2 items-center gap-1.5 rounded-sm border border-ember/60 bg-ink px-2 py-1.5 shadow-[0_12px_24px_-12px_rgba(0,0,0,0.95)]"
        >
          <GripVertical className="h-3 w-3 text-ember" />
          <span className="h-1.5 w-10 rounded-sm bg-cream/50" />
          <MousePointer2 className="h-3 w-3 text-cream/70" />
        </motion.div>
      </div>
    </ArtShell>
  );
}

function AnalyticsDash() {
  const bars = [30, 45, 38, 60, 72, 88, 100];
  return (
    <ArtShell label="ANALYTICS — TRAFFIC & AUDIENCE" meta="LAST 30 DAYS">
      <div className="flex h-full flex-col rounded-md border border-cream/10 bg-night/60 p-3">
        <div className="flex shrink-0 items-center justify-between px-1 pb-1.5">
          <span className="font-mono text-[7px] tracking-[0.2em] text-cream/45">SESSIONS BY WEEK</span>
          <span className="flex items-center gap-1 border border-ember/40 bg-ember/10 px-1.5 py-0.5 font-mono text-[7px] font-bold tracking-[0.1em] text-ember">
            <ArrowUpRight className="h-2.5 w-2.5" /> +42%
          </span>
        </div>
        <div className="flex min-h-0 flex-1 items-center gap-4">
          <div className="relative shrink-0">
            <svg viewBox="0 0 80 80" className="h-16 w-16 md:h-20 md:w-20">
              <circle cx="40" cy="40" r="30" fill="none" stroke="rgba(245,240,225,0.12)" strokeWidth="9" />
              <motion.circle
                cx="40" cy="40" r="30" fill="none" stroke="#E85A24" strokeWidth="9" strokeLinecap="round"
                strokeDasharray="188.5" initial={{ strokeDashoffset: 188.5 }} animate={{ strokeDashoffset: 188.5 * 0.32 }}
                transition={{ duration: 1.1, delay: 0.3 }} transform="rotate(-90 40 40)"
              />
            </svg>
            <span className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="font-mono text-[9px] font-bold tracking-[0.1em] text-cream/80">68%</span>
              <span className="font-mono text-[5px] tracking-[0.2em] text-cream/40">RETURN</span>
            </span>
          </div>
          <div className="flex h-full min-w-0 flex-1 items-end gap-1.5 pb-1">
            {bars.map((h, i) => (
              <motion.span key={i} initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ delay: 0.2 + i * 0.08, duration: 0.5 }} className={`relative w-full rounded-t-[2px] ${i === bars.length - 1 ? "bg-gradient-to-t from-ember/60 to-ember" : "bg-cream/35"}`} style={{ minHeight: "8px" }}>
                {i === bars.length - 1 && (
                  <span className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap bg-cream px-1 font-mono text-[6px] font-bold tracking-[0.1em] text-night">2.4K</span>
                )}
              </motion.span>
            ))}
          </div>
        </div>
        <div className="h-px shrink-0 bg-cream/10" />
      </div>
    </ArtShell>
  );
}

function ListingForm() {
  const fields = [
    { t: "TITLE", w: "w-4/5", i: 0 },
    { t: "PRICE", w: "w-2/5", i: 1 },
    { t: "KEYWORDS", w: "w-3/4", i: 2 },
  ];
  return (
    <ArtShell label="MARKETPLACE LISTING — LIVE" meta="SKU 0042">
      <div className="flex h-full flex-col justify-center gap-2.5 rounded-md border border-cream/10 bg-night/60 p-3.5">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-dashed border-cream/30 bg-ink/50 text-cream/40">
            <ImageIcon className="h-4 w-4" />
          </span>
          <div className="min-w-0 flex-1 space-y-1.5">
            <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.15, duration: 0.45 }} className="h-2 w-4/5 origin-left rounded-sm bg-cream/60" />
            <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.28, duration: 0.45 }} className="h-2 w-2/5 origin-left rounded-sm bg-ember/80" />
          </div>
          <span className="flex shrink-0 items-center gap-1 font-mono text-[7px] tracking-[0.15em] text-ember">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ember" /> LIVE
          </span>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {fields.map((f, i) => (
            <motion.div key={f.t} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + i * 0.1 }} className="border border-cream/15 bg-ink/60 px-2 py-1">
              <span className="block font-mono text-[6px] tracking-[0.2em] text-cream/40">{f.t}</span>
              <span className={`mt-1 block h-1 rounded-full ${f.w} ${f.i === 1 ? "bg-ember/80" : "bg-cream/45"}`} />
            </motion.div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-cream/10">
            <motion.div initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ delay: 0.5, duration: 0.8 }} className="h-full rounded-full bg-ember" />
          </div>
          <span className="shrink-0 font-mono text-[7px] tracking-[0.15em] text-cream/50">SYNC 100%</span>
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }} className="relative flex h-6 shrink-0 items-center justify-center overflow-hidden bg-ember font-mono text-[8px] font-bold tracking-[0.2em] text-night">
          PUBLISH LISTING
          <Sheen />
        </motion.div>
      </div>
    </ArtShell>
  );
}

function GrowthCurve() {
  return (
    <ArtShell label="GROWTH CURVE — SEO" meta="12-MONTH TREND">
      <div className="flex h-full flex-col rounded-md border border-cream/10 bg-night/60 p-2.5">
        <div className="flex shrink-0 items-center justify-between px-1 pb-1">
          <span className="font-mono text-[7px] tracking-[0.2em] text-cream/45">ORGANIC CLICKS</span>
          <span className="flex items-center gap-1 border border-ember/40 bg-ember/10 px-1.5 py-0.5 font-mono text-[7px] font-bold tracking-[0.1em] text-ember">
            <ArrowUpRight className="h-2.5 w-2.5" /> +142%
          </span>
        </div>
        <div className="relative min-h-0 flex-1">
          <svg viewBox="0 0 400 200" className="h-full w-full" preserveAspectRatio="none">
            <defs>
              <linearGradient id="gc" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#E85A24" stopOpacity="0.35" />
                <stop offset="100%" stopColor="#E85A24" stopOpacity="0" />
              </linearGradient>
            </defs>
            {[40, 80, 120, 160].map((y) => (
              <line key={y} x1="0" y1={y} x2="400" y2={y} stroke="#F5F0E1" strokeOpacity="0.08" />
            ))}
            <line x1="0" y1="48" x2="400" y2="48" stroke="#F5F0E1" strokeOpacity="0.22" strokeDasharray="4 5" />
            <text x="8" y="42" fill="#F5F0E1" fillOpacity="0.5" style={{ font: "700 9px 'JetBrains Mono', monospace", letterSpacing: "2px" }}>GOAL</text>
            <motion.path d="M0 180 C 60 175 90 150 130 142 S 210 120 250 88 S 340 40 400 18 L 400 200 L 0 200 Z" fill="url(#gc)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5, duration: 0.6 }} />
            <motion.path d="M0 180 C 60 175 90 150 130 142 S 210 120 250 88 S 340 40 400 18" fill="none" stroke="#E85A24" strokeWidth="3" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.1, ease: "easeOut" }} />
            <motion.circle cx="396" cy="20" r="5" fill="#E85A24" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.05 }} />
            <motion.circle cx="396" cy="20" r="9" fill="none" stroke="#E85A24" strokeWidth="2" animate={{ scale: [1, 2.1], opacity: [0.7, 0] }} transition={{ duration: 1.6, repeat: Infinity }} style={{ transformOrigin: "396px 20px" }} />
          </svg>
        </div>
      </div>
    </ArtShell>
  );
}

function CatalogGrid() {
  return (
    <ArtShell label="PRODUCT GRID — CATALOGUE" meta="6 / 48 SKUS">
      <div className="grid h-full grid-cols-3 grid-rows-2 gap-1.5">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 + i * 0.07, duration: 0.35 }}
            className={`relative flex flex-col gap-1 overflow-hidden rounded-md border p-1.5 ${i === 2 ? "border-ember/80 bg-ember/10" : "border-cream/12 bg-night/60"}`}
          >
            <span className="absolute right-1.5 top-1 font-mono text-[5px] tracking-[0.15em] text-cream/30">SKU-0{i + 1}</span>
            <div className={`relative min-h-0 flex-1 overflow-hidden rounded-[3px] bg-gradient-to-br ${i % 2 ? "from-cream/15 to-transparent" : "from-ember/30 to-transparent"}`}>
              {i === 2 && <span className="absolute left-1 top-1 bg-ember px-1 font-mono text-[5px] font-bold tracking-[0.15em] text-night">LIVE</span>}
            </div>
            <span className="block h-1 w-4/5 rounded-sm bg-cream/40" />
            <span className="block h-1 w-2/5 rounded-sm bg-ember/70" />
          </motion.div>
        ))}
      </div>
    </ArtShell>
  );
}

function Timeline() {
  const Clip = ({ left, w, cls, delay }) => (
    <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay, duration: 0.45, ease: [0.22, 1, 0.36, 1] }} className={`absolute top-1/2 h-4 -translate-y-1/2 origin-left rounded-[2px] ${cls}`} style={{ left, width: w }}>
      <span className="absolute inset-y-0 left-0 w-[3px] bg-cream/80" />
      <span className="absolute inset-y-0 right-0 w-[3px] bg-cream/80" />
    </motion.div>
  );
  return (
    <ArtShell label="EDIT TIMELINE — VIDEO CUT" meta="5S LOOP">
      <div className="flex h-full flex-col">
        <div className="relative h-9 w-full shrink-0 overflow-hidden rounded-sm border border-cream/15 bg-ink">
          <div className="absolute inset-0 bg-gradient-to-br from-ember/25 via-transparent to-cream/5" />
          <TopoLines className="pointer-events-none absolute inset-0 h-full w-full opacity-20" />
          <span className="absolute left-2 top-1.5 font-mono text-[7px] tracking-[0.25em] text-cream/70">PREVIEW — STORYTELLING CUT</span>
          <span className="absolute right-2 top-1.5 flex items-center gap-1.5 font-mono text-[7px] tracking-[0.2em] text-ember">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ember" /> 00:00:04:12
          </span>
        </div>
        <div className="mt-1 flex shrink-0 justify-between px-7">
          {Array.from({ length: 14 }).map((_, i) => (
            <span key={i} className={`w-px bg-cream/25 ${i % 3 === 0 ? "h-1.5" : "h-0.5"}`} />
          ))}
        </div>
        <div className="relative mt-1 min-h-0 flex-1 space-y-1.5">
          <motion.div initial={{ left: "0%" }} animate={{ left: "100%" }} transition={{ duration: 5, repeat: Infinity, ease: "linear" }} className="absolute bottom-0 top-0 z-10 w-px bg-ember shadow-[0_0_8px_rgba(232,90,36,0.8)]" data-testid="craft-timeline-playhead">
            <span className="absolute -left-[3px] top-0 h-2 w-[7px] bg-ember" />
          </motion.div>
          <div className="flex items-center gap-2">
            <span className="w-5 text-right font-mono text-[7px] font-bold tracking-[0.15em] text-ember/70">V2</span>
            <div className="relative h-7 flex-1 overflow-hidden rounded-sm border border-cream/10 bg-night/60">
              <Clip left="4%" w="26%" cls="bg-ember/80" delay={0.1} />
              <Clip left="33%" w="42%" cls="bg-cream/30" delay={0.2} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-5 text-right font-mono text-[7px] tracking-[0.15em] text-cream/35">V1</span>
            <div className="relative h-7 flex-1 overflow-hidden rounded-sm border border-cream/10 bg-night/60">
              <Clip left="4%" w="18%" cls="bg-cream/50" delay={0.15} />
              <Clip left="25%" w="40%" cls="bg-ember/50" delay={0.25} />
              <Clip left="68%" w="28%" cls="bg-cream/25" delay={0.35} />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-5 text-right font-mono text-[7px] tracking-[0.15em] text-cream/35">A1</span>
            <div className="flex h-7 flex-1 items-center gap-[3px] overflow-hidden rounded-sm border border-cream/10 bg-night/60 px-1.5">
              {Array.from({ length: 44 }).map((_, i) => (
                <motion.span key={i} initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} transition={{ delay: 0.4 + i * 0.012, duration: 0.3 }} className="w-[2px] shrink-0 origin-center bg-gradient-to-b from-ember/70 to-cream/40" style={{ height: `${6 + Math.abs(Math.sin(i * 1.7)) * 14}px` }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </ArtShell>
  );
}

function Storyboard() {
  const beats = [
    { label: "HOOK", dur: "0–3S", bars: ["w-3/4", "w-1/2"] },
    { label: "JOURNEY", dur: "3–9S", bars: ["w-full", "w-2/3"] },
    { label: "PAYOFF", dur: "9–12S", bars: ["w-2/3", "w-1/3"] },
  ];
  return (
    <ArtShell label="STORYBOARD — SCENE FLOW" meta="3 BEATS">
      <div className="flex h-full items-center justify-center gap-2">
        {beats.map((b, i) => (
          <div key={b.label} className="flex items-center gap-2">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 + i * 0.18, duration: 0.4 }} className="w-16 space-y-1 md:w-20">
              <div className={`relative h-16 overflow-hidden rounded-sm border p-1.5 md:h-20 ${i === 2 ? "border-ember/80 bg-ember/15" : "border-cream/15 bg-night/60"}`}>
                <TopoLines className="pointer-events-none absolute inset-0 h-full w-full opacity-15" />
                <span className={`absolute right-1.5 top-1.5 h-2.5 w-2.5 rounded-full ${i === 2 ? "bg-cream" : "bg-ember/90"} shadow-[0_0_8px_rgba(232,90,36,0.7)]`} />
                <span className={`absolute bottom-1 left-1.5 block h-1 ${b.bars[0]} rounded-sm bg-cream/60`} />
                <span className={`absolute bottom-3 left-1.5 block h-1 ${b.bars[1]} rounded-sm bg-cream/30`} />
                <span className="absolute left-1.5 top-1 font-mono text-[6px] font-bold tracking-[0.15em] text-ember">0{i + 1}</span>
              </div>
              <p className="flex items-center justify-center gap-1 text-center font-mono text-[7px] font-bold tracking-[0.2em] text-cream/60">
                {b.label}<span className="text-cream/30">{b.dur}</span>
              </p>
            </motion.div>
            {i < 2 && (
              <motion.span animate={{ x: [0, 4, 0] }} transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.3 }} className="shrink-0 text-ember/80">
                <MoveRight className="h-3.5 w-3.5" />
              </motion.span>
            )}
          </div>
        ))}
      </div>
    </ArtShell>
  );
}

function Studio() {
  const rows = [
    { w: "62%", views: "1.2K", bar: "78%", dur: "4:12" },
    { w: "48%", views: "864", bar: "52%", dur: "7:48" },
    { w: "71%", views: "2.4K", bar: "91%", dur: "12:05" },
  ];
  return (
    <ArtShell label="CHANNEL DASHBOARD — YOUTUBE" meta="UPLOADS LIVE">
      <div className="flex h-full flex-col justify-center gap-1.5">
        <div className="flex shrink-0 items-center justify-between">
          <span className="flex items-center gap-2 font-mono text-[8px] tracking-[0.25em] text-cream/60">
            <span className="flex h-4 w-4 items-center justify-center rounded-full bg-ember/80">
              <Youtube className="h-2.5 w-2.5 text-night" />
            </span>
            @STANDOUT_HIKER
          </span>
          <span className="flex items-center gap-1.5 border border-cream/15 px-2 py-0.5 font-mono text-[7px] tracking-[0.2em] text-cream/50">
            <span className="h-1 w-1 animate-pulse rounded-full bg-ember" /> UPLOADS LIVE
          </span>
        </div>
        {rows.map((r, i) => (
          <motion.div key={i} initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.12, duration: 0.4 }} className="flex flex-1 items-center gap-3 rounded-md border border-cream/10 bg-night/60 p-2">
            <span className="relative flex h-9 w-12 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-gradient-to-br from-ember/40 to-cream/10">
              <Play className="h-3.5 w-3.5 fill-current text-cream/85" />
              <span className="absolute bottom-0.5 right-1 bg-night/85 px-1 font-mono text-[5px] tracking-[0.1em] text-cream/80">{r.dur}</span>
            </span>
            <div className="min-w-0 flex-1">
              <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 0.2 + i * 0.12, duration: 0.5 }} className="h-2 origin-left rounded-sm bg-cream/60" style={{ width: r.w }} />
              <div className="mt-1.5 flex items-center gap-2">
                <div className="h-1 flex-1 overflow-hidden rounded-full bg-cream/10">
                  <motion.div initial={{ width: 0 }} animate={{ width: r.bar }} transition={{ delay: 0.35 + i * 0.12, duration: 0.7 }} className="h-full rounded-full bg-gradient-to-r from-ember/70 to-ember" />
                </div>
                <span className="shrink-0 font-mono text-[7px] tracking-[0.15em] text-cream/50">{r.views} VIEWS</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </ArtShell>
  );
}

const AI_LINES = [
  "> exploring generative tools…",
  "> testing: idea → wireframe",
  "> automating the boring routes",
  "> status: curious, always",
];

function AITerminal() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setCount((c) => (c + 1) % (AI_LINES.length + 2)), 850);
    return () => clearInterval(t);
  }, []);
  return (
    <ArtShell label="EXPLORATION LOG — AI TOOLS" meta="SESSION 07">
      <div className="relative flex h-full flex-col overflow-hidden rounded-md border border-cream/15 bg-night/80">
        <TopoLines className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.06]" />
        <div className="flex shrink-0 items-center gap-1.5 border-b border-cream/10 px-3 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-ember" />
          <span className="h-1.5 w-1.5 rounded-full bg-cream/30" />
          <span className="h-1.5 w-1.5 rounded-full bg-cream/30" />
          <span className="ml-1.5 font-mono text-[7px] tracking-[0.25em] text-cream/45">AI-EXPLORER</span>
          <span className="ml-auto flex items-center gap-1 font-mono text-[7px] tracking-[0.2em] text-ember/80">
            <span className="h-1 w-1 animate-pulse rounded-full bg-ember" /> ONLINE
          </span>
        </div>
        <div className="flex min-h-0 flex-1 flex-col justify-center gap-2 px-4 py-2">
          {AI_LINES.slice(0, count).map((l, i) => (
            <motion.p key={`${l}-${count}`} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }} className="font-mono text-[10px] tracking-[0.08em] text-cream/80 md:text-[11px]">
              <span className="text-ember">&gt;</span>{l.slice(1)}
            </motion.p>
          ))}
          {count <= AI_LINES.length && <span className="h-3.5 w-2 animate-pulse bg-ember" />}
        </div>
        <div className="shrink-0 border-t border-cream/10 px-3 py-1.5">
          <span className="font-mono text-[7px] tracking-[0.2em] text-cream/40">MODEL: CURIOSITY v2 · SCANNING TOOLSTACK…</span>
        </div>
      </div>
    </ArtShell>
  );
}

/* sheen sweep — premium hover-state light pass for artifact buttons */
function Sheen() {
  return (
    <motion.span aria-hidden="true" animate={{ x: ["-160%", "160%"] }} transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut", repeatDelay: 1 }} className="pointer-events-none absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
  );
}

/* shared shell: consistent framing + readable caption */
function ArtShell({ label, meta, children }) {
  return (
    <div className="flex h-full flex-col p-3 md:p-3.5">
      <div className="relative min-h-0 flex-1 overflow-hidden rounded-md border border-cream/12 bg-[#0C120E] shadow-[inset_0_1px_0_rgba(245,240,225,0.07),0_14px_34px_-20px_rgba(0,0,0,0.95)]">
        <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: "linear-gradient(rgba(245,240,225,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(245,240,225,0.05) 1px, transparent 1px)", backgroundSize: "16px 16px" }} />
        <div className="pointer-events-none absolute -right-12 -top-12 h-36 w-36 rounded-full bg-ember/10 blur-2xl" />
        <div className="relative h-full">{children}</div>
      </div>
      <div className="mt-2 flex shrink-0 items-center justify-between gap-3">
        <p className="truncate font-mono text-[10px] font-bold tracking-[0.22em] text-cream/60">{label}</p>
        <span className="shrink-0 font-mono text-[8px] tracking-[0.25em] text-ember/80">{meta ?? "RENDER · LIVE"}</span>
      </div>
    </div>
  );
}

const ARTIFACTS = { design: AdobeCanvas, web: Wireframe, growth: GrowthCurve, content: Timeline };
const SKILL_ARTIFACTS = {
  "Adobe Creative Suite & Canva": AdobeCanvas,
  "Web Design": Wireframe,
  "UI Design": UIKit,
  "Social Media Creatives": SocialPost,
  "WordPress": WordPressEditor,
  "Shopify": ShopifyStore,
  "Elementor": ElementorDrag,
  "Google Analytics": AnalyticsDash,
  "Ecommerce Listing": ListingForm,
  "SEO": GrowthCurve,
  "Product Catalogue": CatalogGrid,
  "Video Editing": Timeline,
  "Visual Storytelling": Storyboard,
  "YouTube Management": Studio,
  "AI Tools & Exploration": AITerminal,
};
const TAGS = { design: "DESIGN", web: "WEBSITES", growth: "GROWTH", content: "CONTENT" };

export default function Skills() {
  const [auto, setAuto] = useState(0);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setAuto((a) => (a + 1) % ALL.length), 2600);
    return () => clearInterval(t);
  }, []);

  const active = hovered ?? auto;
  const current = ALL[active];
  const Artifact = SKILL_ARTIFACTS[current.s] ?? ARTIFACTS[current.pack];
  const artifactId = current.s.toLowerCase().replace(/[^a-z]+/g, "-");

  return (
    <section id="skills" data-testid="skills-section" className="relative scroll-mt-24 py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <SectionHeader
          index="05"
          station="Tools and Skills"
          id="BENCH // 15 TOOLS — 15 LIVE RENDERS"
          title={<>Field Kit<br /><span className="text-ember">Tools &amp; Skills</span></>}
          sub="A list can't show craft — so this bench builds it. Hover a tool and the viewport renders it in code — every tool gets its own scene. Design, development and content — proven on the spot."
        />

        <div className="grid items-center gap-8 lg:grid-cols-[1.15fr_1fr] lg:gap-12">
          {/* tool chips — all 15, grouped, readable */}
          <Reveal>
            <div data-testid="craft-tool-list">
              {SKILL_PACKS.map((p) => (
                <div key={p.id} className="mt-4 first:mt-0">
                  <p className="mb-2 flex items-center gap-2 font-mono text-[9px] font-bold tracking-[0.3em] text-ember">
                    <span className="h-px w-4 bg-ember/60" /> {p.name.toUpperCase()}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {p.skills.map((s) => {
                      const idx = ALL.findIndex((a) => a.s === s && a.pack === p.id);
                      const on = active === idx;
                      return (
                        <button
                          key={s}
                          onMouseEnter={() => setHovered(idx)}
                          onMouseLeave={() => setHovered(null)}
                          onFocus={() => setHovered(idx)}
                          onBlur={() => setHovered(null)}
                          onClick={() => setHovered(idx)}
                          data-testid={`craft-skill-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                          className={`flex items-center gap-2 border px-3 py-2 text-xs font-medium transition-all duration-200 ${on ? "border-ember bg-ember text-night" : "border-white/12 bg-ink/50 text-cream/90 hover:border-ember/60 hover:text-white"}`}
                        >
                          <span className={`font-mono text-[9px] tracking-[0.08em] ${on ? "text-night/60" : "text-ember/80"}`}>{ALL[idx].code}</span>
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>

          {/* live viewport */}
          <Reveal delay={0.1}>
            <div className="border border-cream/10 bg-ink/70" data-testid="craft-viewport">
              <div className="flex items-center justify-between border-b border-cream/10 px-4 py-2.5">
                <span className="flex items-center gap-2.5 font-mono text-[10px] font-bold tracking-[0.25em] text-cream/60">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute h-2 w-2 animate-ping rounded-full bg-ember opacity-60" />
                    <span className="h-2 w-2 rounded-full bg-ember" />
                  </span>
                  LIVE RENDER
                </span>
                <span className="font-mono text-[10px] tracking-[0.25em] text-cream/60">{TAGS[current.pack]}</span>
              </div>

              <div className="h-[250px] md:h-[290px]" key={artifactId} data-testid={`craft-artifact-${artifactId}`}>
                <Artifact />
              </div>

              <div className="flex items-end justify-between gap-4 border-t border-cream/10 px-4 py-3">
                <div>
                  <p className="font-mono text-[9px] font-bold tracking-[0.25em] text-ember">{TAGS[current.pack]} — ON THE BENCH</p>
                  <p className="mt-1 font-display text-xl font-black uppercase leading-none text-white md:text-2xl" data-testid="craft-active-skill">
                    {current.s}
                  </p>
                </div>
                <span className="font-mono text-[9px] tracking-[0.2em] text-cream/40">{current.code}</span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
